import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, Frame, Loading, Page, Navigation as PolarisNavigation, Box, InlineStack, Text, Button, ActionList, Popover } from '@shopify/polaris';
import { HomeIcon, ChartVerticalIcon, OrderIcon, ExitIcon, FilterIcon, CashDollarIcon, ShippingLabelIcon, ReceiptIcon } from '@shopify/polaris-icons';
import enTranslations from '@shopify/polaris/locales/en.json';

// Import providers
import QueryProvider, { handleAuthChange, prefetchCommonData } from './providers/QueryProvider';
import ErrorBoundary, { APIErrorBoundary } from './components/ErrorBoundary';
import SessionManager from './components/SessionManager';
import { secureAuthUtils } from './utils/secureAuth';
import apiClient from './utils/api';

// Import pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import BrandConfiguration from './pages/BrandConfiguration';
import MarketplacePatterns from './pages/MarketplacePatterns';
// Import new analytics pages
import Analytics from './pages/Analytics';
import OrderManagement from './pages/OrderManagement';
import CommissionAdjustments from './pages/CommissionAdjustments';
import Invoices from './pages/Invoices';
import { useBrands } from './utils/queries';
import { ShippingAndReturn } from './pages/ShippingAndReturn';

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [userMenuActive, setUserMenuActive] = useState(false);
  const location = useLocation();

  // Fetch brands for sidebar navigation (only when authenticated)
  const { data: brandsResponse, isLoading: brandsLoading, error: brandsError } = useBrands({}, {
    enabled: isAuthenticated
  });

  const brands = brandsResponse?.brands || brandsResponse || [];

  // Log any brand loading errors in development
  useEffect(() => {
    if (brandsError && process.env.NODE_ENV === 'development') {
      console.error('Brands loading error:', brandsError);
    }
  }, [brandsError]);

  useEffect(() => {
    // Initialize authentication from secure storage
    const initializeAuth = async () => {
      try {
        const token = await secureAuthUtils.getToken();
        const userData = await secureAuthUtils.getUserData();

        if (token && userData && secureAuthUtils.isSessionValid()) {
          setIsAuthenticated(true);
          setUser(userData);
        } else if (token || userData) {
          // Clear invalid session
          secureAuthUtils.clearSession();
        }
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
        secureAuthUtils.clearSession();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const handleLogin = async (token, userData) => {
    // Use secure storage for authentication data
    await secureAuthUtils.setToken(token, userData);

    // Set token directly in apiClient memory to prevent race condition
    // This ensures the token is available before any API calls are triggered
    apiClient.token = token;

    setIsAuthenticated(true);
    setUser(userData);

    // Handle query cache for authenticated user
    handleAuthChange(true);

    // Prefetch common data for better UX
    try {
      await prefetchCommonData();
    } catch (error) {
      console.warn('Failed to prefetch data:', error);
    }
  };

  const handleLogout = (reason = 'user_requested') => {
    // Clear secure authentication data
    secureAuthUtils.clearSession();
    setIsAuthenticated(false);
    setUser(null);

    // Clear all query caches
    handleAuthChange(false);

    // Force a page reload to ensure clean state - use absolute path for security
    window.location.href = `${window.location.origin}/login`;
  };

  const toggleMobileNavigation = () => {
    setMobileNavigationActive(!mobileNavigationActive);
  };

  if (loading) {
    return (
      <Frame>
        <Loading />
      </Frame>
    );
  }

  if (!isAuthenticated) {
    return (
      <Page>
        <Login onLogin={handleLogin} />
      </Page>
    );
  }

  // Custom header to replace deprecated TopBar
  const headerMarkup = (
    <Box background="bg-surface" paddingBlock="400" paddingInline="600">
      <InlineStack align="space-between" blockAlign="center">
        <InlineStack gap="400" align="start">
          <Button
            variant="tertiary"
            onClick={toggleMobileNavigation}
            icon={HomeIcon}
            accessibilityLabel="Toggle navigation"
          />
          <Text variant="headingMd" fontWeight="semibold">
            2W Admin Dashboard
          </Text>
        </InlineStack>

        <Popover
          active={userMenuActive}
          activator={
            <Button
              variant="tertiary"
              onClick={() => setUserMenuActive(!userMenuActive)}
              disclosure="down"
            >
              {user?.name || 'Admin User'}
            </Button>
          }
          onClose={() => setUserMenuActive(false)}
        >
          <ActionList
            items={[
              {
                content: user?.email || 'Administrator',
                disabled: true,
              },
              {
                content: 'Logout',
                icon: ExitIcon,
                onAction: () => {
                  setUserMenuActive(false);
                  handleLogout();
                },
              },
            ]}
          />
        </Popover>
      </InlineStack>
    </Box>
  );

  // Sidebar navigation matching exact specification
  const navigationMarkup = (
    <PolarisNavigation location={location.pathname}>
      <PolarisNavigation.Section
        title="2W Overview"
        items={[
          {
            label: 'Dashboard',
            icon: HomeIcon,
            url: '/',
            exactMatch: true,
            selected: location.pathname === '/',
          },
          {
            label: 'Analytics',
            icon: ChartVerticalIcon,
            url: '/analytics',
            selected: location.pathname === '/analytics',
          },
          {
            label: 'Orders Management',
            icon: OrderIcon,
            url: '/orders',
            selected: location.pathname === '/orders',
          },
          {
            label: 'Marketplace Settings',
            icon: FilterIcon,
            url: '/marketplace',
            selected: location.pathname === '/marketplace' || location.pathname.startsWith('/marketplace/'),
          },
          {
            label: 'Commission Adjustments',
            icon: CashDollarIcon,
            url: '/commission-adjustments',
            selected: location.pathname === '/commission-adjustments',
          },
          {
            label: 'Shipping & Return',
            icon: ShippingLabelIcon,
            url: '/shipping-and-return',
            selected: location.pathname === '/shipping-and-return',
          },
          {
            label: 'Invoices',
            icon: ReceiptIcon,
            url: '/invoices',
            selected: location.pathname === '/invoices',
          },
        ]}
      />

      {/* Brands Section - Collapsible to prevent clutter */}
      {brandsLoading && (
        <PolarisNavigation.Section
          title="Brands"
          items={[
            {
              label: 'Loading brands...',
              disabled: true
            }
          ]}
        />
      )}
      {!brandsLoading && Array.isArray(brands) && brands.length > 0 && (
        <PolarisNavigation.Section
          title="Brands"
          items={brands
            .filter(brand => brand && brand.id && brand.name)
            .map((brand) => ({
              label: brand.name,
              url: `/brands/${brand.id}`,
              selected: location.pathname === `/brands/${brand.id}`,
            }))
          }
        />
      )}
      {!brandsLoading && brandsError && (
        <PolarisNavigation.Section
          title="Brands"
          items={[
            {
              label: 'Error loading brands',
              disabled: true
            }
          ]}
        />
      )}
    </PolarisNavigation>
  );

  return (
    <Frame
      navigation={navigationMarkup}
      showMobileNavigation={mobileNavigationActive}
      onNavigationDismiss={toggleMobileNavigation}
    >
      <SessionManager
        onLogout={handleLogout}
        onSessionExtended={() => console.log('Session extended')}
      />
      {headerMarkup}
      <ErrorBoundary
        title="Page Loading Error"
        fallbackComponent="AppRoutes"
        level="error"
      >
        <Routes>
          <Route path="/" element={
            <APIErrorBoundary>
              <Dashboard />
            </APIErrorBoundary>
          } />
          <Route path="/analytics" element={
            <APIErrorBoundary>
              <Analytics />
            </APIErrorBoundary>
          } />
          <Route path="/brands" element={<Navigate to="/" replace />} />
          <Route path="/brands/:brandId" element={
            <APIErrorBoundary>
              <BrandConfiguration />
            </APIErrorBoundary>
          } />
          <Route path="/marketplace" element={
            <APIErrorBoundary>
              <MarketplacePatterns />
            </APIErrorBoundary>
          } />
          <Route path="/orders" element={
            <APIErrorBoundary>
              <OrderManagement />
            </APIErrorBoundary>
          } />
          <Route path="/commission-adjustments" element={
            <APIErrorBoundary>
              <CommissionAdjustments />
            </APIErrorBoundary>
          } />
          <Route path="/shipping-and-return" element={
            <APIErrorBoundary>
              <ShippingAndReturn />
            </APIErrorBoundary>
          } />
          <Route path="/invoices" element={
            <APIErrorBoundary>
              <Invoices />
            </APIErrorBoundary>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </Frame>
  );
}

function App() {
  return (
    <QueryProvider>
      <AppProvider
        i18n={enTranslations}
      >
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppContent />
        </Router>
      </AppProvider>
    </QueryProvider>
  );
}

export default App;